import { FeatureModule, FeatureModuleSave, InternalPackage, InternalRole, PackageCreate, RoleCreate, StoredAfterWrite } from '../features/plans/types/Plans'
import { internalAccountApi } from './internal-account'

const plansApi = internalAccountApi
  .enhanceEndpoints({ addTagTypes: ['internalPackages', 'internalRoles', 'internalFeatureModules', 'internalOrganizations'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getInternalPackages: builder.query<InternalPackage[], void>({
        query: () => 'packages',
        providesTags: ['internalPackages'],
      }),
      // The backend answers with an empty body and rejects a name another package already has, so the created package is found by name.
      createInternalPackage: builder.mutation<StoredAfterWrite<InternalPackage>, PackageCreate>({
        async queryFn(planPackage, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'packages', method: 'POST', body: planPackage, responseHandler: 'text' })
          if (saved.error) return { error: saved.error }
          const packages = await baseQuery('packages')
          if (packages.error) return { error: packages.error }
          return { data: { stored: (packages.data as InternalPackage[]).find(({ name }) => name === planPackage.name) } }
        },
        invalidatesTags: ['internalPackages'],
      }),
      // Replaces the stored package with the complete one sent.
      updateInternalPackage: builder.mutation<StoredAfterWrite<InternalPackage>, InternalPackage>({
        async queryFn(planPackage, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'packages', method: 'PUT', body: planPackage, responseHandler: 'text' })
          if (saved.error) return { error: saved.error }
          const packages = await baseQuery('packages')
          if (packages.error) return { error: packages.error }
          return { data: { stored: (packages.data as InternalPackage[]).find(({ id }) => id === planPackage.id) } }
        },
        invalidatesTags: ['internalPackages', 'internalOrganizations'],
      }),
      deleteInternalPackage: builder.mutation<StoredAfterWrite<InternalPackage>, string>({
        async queryFn(packageId, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `packages/${packageId}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const packages = await baseQuery('packages')
          if (packages.error) return { error: packages.error }
          return { data: { stored: (packages.data as InternalPackage[]).find(({ id }) => id === packageId) } }
        },
        invalidatesTags: ['internalPackages', 'internalOrganizations'],
      }),

      getInternalRoles: builder.query<InternalRole[], void>({
        query: () => 'roles',
        providesTags: ['internalRoles'],
      }),
      // Answers with an empty body; a taken name is rejected with 400.
      createInternalRole: builder.mutation<StoredAfterWrite<InternalRole>, RoleCreate>({
        async queryFn(role, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'roles', method: 'POST', body: role, responseHandler: 'text' })
          if (saved.error) return { error: saved.error }
          const roles = await baseQuery('roles')
          if (roles.error) return { error: roles.error }
          return { data: { stored: (roles.data as InternalRole[]).find(({ name }) => name === role.name) } }
        },
        invalidatesTags: ['internalRoles'],
      }),
      updateInternalRole: builder.mutation<StoredAfterWrite<InternalRole>, InternalRole>({
        async queryFn(role, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'roles', method: 'PUT', body: role })
          if (saved.error) return { error: saved.error }
          const roles = await baseQuery('roles')
          if (roles.error) return { error: roles.error }
          return { data: { stored: (roles.data as InternalRole[]).find(({ name }) => name === role.name) } }
        },
        invalidatesTags: ['internalRoles'],
      }),
      // The backend also removes the role from every package and module that has it.
      deleteInternalRole: builder.mutation<StoredAfterWrite<InternalRole>, string>({
        async queryFn(roleName, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `roles/${encodeURIComponent(roleName)}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const roles = await baseQuery('roles')
          if (roles.error) return { error: roles.error }
          return { data: { stored: (roles.data as InternalRole[]).find(({ name }) => name === roleName) } }
        },
        invalidatesTags: ['internalRoles', 'internalPackages', 'internalFeatureModules', 'internalOrganizations'],
      }),

      getInternalFeatureModules: builder.query<FeatureModule[], void>({
        query: () => 'featuremodules',
        providesTags: ['internalFeatureModules'],
      }),
      saveInternalFeatureModule: builder.mutation<StoredAfterWrite<FeatureModule>, FeatureModuleSave>({
        async queryFn(featureModule, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'featuremodules', method: 'POST', body: featureModule })
          if (saved.error) return { error: saved.error }
          const savedId = featureModule.id ?? (saved.data as FeatureModule | null)?.id
          const modules = await baseQuery('featuremodules')
          if (modules.error) return { error: modules.error }
          const stored = (modules.data as FeatureModule[]).find(({ id, name }) => (savedId ? id === savedId : name === featureModule.name))
          return { data: { stored } }
        },
        invalidatesTags: ['internalFeatureModules'],
      }),
      deleteInternalFeatureModule: builder.mutation<StoredAfterWrite<FeatureModule>, string>({
        async queryFn(moduleId, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `featuremodules/${moduleId}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const modules = await baseQuery('featuremodules')
          if (modules.error) return { error: modules.error }
          return { data: { stored: (modules.data as FeatureModule[]).find(({ id }) => id === moduleId) } }
        },
        invalidatesTags: ['internalFeatureModules'],
      }),
    }),
  })

export const {
  useGetInternalPackagesQuery,
  useCreateInternalPackageMutation,
  useUpdateInternalPackageMutation,
  useDeleteInternalPackageMutation,
  useGetInternalRolesQuery,
  useCreateInternalRoleMutation,
  useUpdateInternalRoleMutation,
  useDeleteInternalRoleMutation,
  useGetInternalFeatureModulesQuery,
  useSaveInternalFeatureModuleMutation,
  useDeleteInternalFeatureModuleMutation,
} = plansApi
